import Vue from 'vue';
import moment from 'moment';
import * as giftConsts from '@/constants/Gifting';
import GiftingService from '@/services/gifting/GiftingService';
import PostcodeService from '@/services/logistics/PostcodeService';
import { PRODUCT_TYPE_MULTI, PRODUCT_TYPE_SINGLE } from '@/constants/Gifting';

const dayMap = [
    {
        day: 'Sunday',
        index: 0,
    },
    {
        day: 'Monday',
        index: 1,
    },
    {
        day: 'Tuesday',
        index: 2,
    },
    {
        day: 'Wednesday',
        index: 3,
    },
    {
        day: 'Thursday',
        index: 4,
    },
    {
        day: 'Friday',
        index: 5,
    },
    {
        day: 'Saturday',
        index: 6,
    },
];

const getDefaultState = () => {
    return {
        sections: {
            [giftConsts.SECTION_CHOOSE_A_GIFT]: false,
            [giftConsts.SECTION_NUMBER_OF_DELIVERIES]: false,
            [giftConsts.SECTION_DELIVERY_ADDRESS]: false,
            [giftConsts.SECTION_FREDDIES_ADDONS]: false,
            [giftConsts.SECTION_FREDDIES_SHOP]: false,
            [giftConsts.SECTION_DELIVERY_DATE]: false,
            [giftConsts.SECTION_GIFT_CARD]: false,
        },
        giftDetails: {
            additionalProducts: {},
            additionalProductsCost: 0,
            deliveryType: null,
            subscription: {
                frequency: null,
                numberOfMonths: 1,
                availableDeliveryDay: null,
            },
            addons: {
                paidVase: false,
            },
            contact: {
                mobileNumber: null,
                notifier: 'You',
            },
            hasProcessed: false,
            giftReference: null,
            giftNoteUrl: null,
        },
        prices: {
            haveFetched: false,
            paidVase: null,
            oneOff: null,
            subscription: null,
        },
        message: {
            to: null,
            body: null,
            from: null,
        },
        logistics: {
            firstDeliveryDateAvailable: null,
            deliveryDateSelected: null,
        },
        deliveryDays: [],
        paymentSources: [],
        incompleteGiftingId: null,
        incompleteGifting: {
            total_steps: null,
            current_step: null,
            data: {
                type: null,
                first_name: null,
                last_name: null,
                gift_note_url: null,
                paid_vase: null,
                frequency: null,
                number_of_months: null,
                address: {
                    address_1: null,
                    address_2: null,
                    city: null,
                    country: null,
                    postcode: null,
                },
                delivery: {
                    delivery_date: null,
                    text_to_me: null,
                    telephone: null,
                },
                gift: {
                    from: null,
                    body: null,
                    to: null,
                },
            },
        },
        coupons: {
            multiGift: null,
            singleGift: null,
        },
    };
};

let postcodeService = new PostcodeService();
let giftingService = new GiftingService();

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },

        setAddonVase(state, payload) {
            state.giftDetails.addons.paidVase = payload;
        },

        setDeliveryDateSelected(state, payload) {
            state.logistics.deliveryDateSelected = payload;
        },

        setDeliveryType(state, payload) {
            state.giftDetails.deliveryType = payload;
        },

        setDeliveryDays(state, payload) {
            state.deliveryDays = payload;
        },

        setFirstDeliveryDateAvailable(state, payload) {
            state.logistics.firstDeliveryDateAvailable = payload;
        },

        setGiftNoteUrl(state, payload) {
            state.giftDetails.giftNoteUrl = payload;
        },

        setGiftProcessed(state, payload) {
            state.giftDetails.hasProcessed = true;
        },

        setGiftReference(state, payload) {
            state.giftDetails.giftReference = payload;
        },

        setNotifier(state, payload) {
            state.giftDetails.contact.notifier = payload;
        },

        setNotifierNumber(state, payload) {
            state.giftDetails.contact.mobileNumber = payload;
        },

        setProductPrices(state, payload) {
            for (let i = 0; i < payload.length; i++) {
                let item = payload[i];

                switch (item.name) {
                case 'paidVase':
                    Vue.set(state.prices, 'paidVase', item.price);
                    break;
                case 'oneDelivery':
                    Vue.set(state.prices, 'oneOff', item.price);
                    break;
                case 'multipleDeliveries':
                    Vue.set(state.prices, 'subscription', item.price);
                    break;
                }
            }
        },

        setSectionComplete(state, payload) {
            state.sections[payload] = true;
        },

        setSubscriptionFrequency(state, payload) {
            state.giftDetails.subscription.frequency = payload;
        },

        setSubscriptionNumberOfMonths(state, payload) {
            state.giftDetails.subscription.numberOfMonths = payload;
        },

        updateMessageBody(state, payload) {
            state.message.body = payload;
        },

        updateMessageFrom(state, payload) {
            state.message.from = payload;
        },

        updateMessageTo(state, payload) {
            state.message.to = payload;
        },

        updateAvailableDeliveryDay(state, payload) {
            state.giftDetails.subscription.availableDeliveryDay = payload;
        },

        updateProducts(state, payload) {
            state.giftDetails.additionalProducts = payload;
        },

        updateCurrentStep(state, payload) {
            state.incompleteGifting.current_step = payload;
        },

        updateTotalSteps(state, payload) {
            state.incompleteGifting.total_steps = payload;
        },

        updateProductsTotalCost(state) {
            let price = 0;

            for (let key in state.giftDetails.additionalProducts) {
                price += parseFloat(state.giftDetails.additionalProducts[key].price) * parseFloat(state.giftDetails.additionalProducts[key].quantity);
            }

            state.giftDetails.additionalProductsCost = parseFloat(price.toFixed(2));
        },

        updateGiftingAddress(state, payload) {
            state.incompleteGifting.data.first_name = payload.first_name;
            state.incompleteGifting.data.last_name = payload.last_name;
            state.incompleteGifting.data.address.address_1 = payload.address1;
            state.incompleteGifting.data.address.address_2 = payload.address2;
            state.incompleteGifting.data.address.city = payload.city;
            state.incompleteGifting.data.address.country = payload.country;
            state.incompleteGifting.data.address.postcode = payload.postcode;
        },

        updateIncompleteGiftingData(state, payload) {
            state.incompleteGifting.data.type = state.giftDetails.deliveryType;
            state.incompleteGifting.data.delivery.delivery_date = state.logistics.deliveryDateSelected;
            state.incompleteGifting.data.delivery.text_to_me = state.giftDetails.contact.notifier === 'You';
            state.incompleteGifting.data.delivery.telephone = state.giftDetails.contact.mobileNumber;
            state.incompleteGifting.data.gift.from = state.message.from;
            state.incompleteGifting.data.gift.body = state.message.body;
            state.incompleteGifting.data.gift.to = state.message.to;
            state.incompleteGifting.data.gift_note_url = state.giftDetails.giftNoteUrl;
            state.incompleteGifting.data.paid_vase = state.giftDetails.addons.paidVase;
            state.incompleteGifting.data.frequency = state.giftDetails.subscription.frequency;
            state.incompleteGifting.data.number_of_months = state.giftDetails.subscription.numberOfMonths;
        },
        setShowHolidayBoxModal(state, payload) {
            state.holidayBox.showHolidayBoxModal = payload;
        },

        setincompleteGiftingId(state, payload) {
            state.incompleteGiftingId = payload;
        },

        setSingleGiftCoupon(state, payload) {
            state.coupons.singleGift = payload;
        },

        setMultiGiftCoupon(state, payload) {
            state.coupons.multiGift = payload;
        },
    },
    actions: {
        async createGift({ commit }, payload) {
            return await new Promise(function(resolve, reject) {
                giftingService.createGift(payload)
                    .then(response => resolve(response))
                    .catch(error => reject(error));
            });
        },

        async fetchAvailableDeliveryDay({ commit, state, rootGetters }, postcode = null) {
            if (!postcode) {
                let selectedAddress = rootGetters['address/getSelectedGiftingAddress'];

                postcode = selectedAddress.postcode;
            }

            return await new Promise(function(resolve, reject) {
                postcodeService.getAvailableDeliveryDay(postcode)
                    .then(response => {
                        commit('updateAvailableDeliveryDay', response.data.message);
                    })
                    .catch(error => reject(error));
            });
        },

        async fetchFirstDeliveryDateAvailable({ commit, state, rootGetters }, forceReload = false) {
            if (state.prices.haveFetched === false || forceReload === true) {
                let selectedAddress = rootGetters['address/getSelectedGiftingAddress'];
                let postcode = selectedAddress.postcode;

                return await new Promise(function(resolve, reject) {
                    let date = postcodeService.legacyGetFirstDeliveryDayForPostcode(postcode);

                    if (date !== false) {
                        commit('setFirstDeliveryDateAvailable', date);
                    }

                    reject(date);
                });
            }
        },

        async fetchPrices({ commit, state }, forceReload = false) {
            if (state.prices.haveFetched === false || forceReload === true) {
                state.haveFetched = true;

                return await new Promise(function(resolve, reject) {
                    giftingService.fetchPrices()
                        .then(response => {
                            commit('setProductPrices', response.data.items);
                            Vue.set(state.prices, 'haveFetched', true);
                        })
                        .catch(error => {
                            Vue.set(state.prices, 'haveFetched', false);
                            reject(error);
                        });
                });
            }
        },

        async fetchDeliveryDays({ commit, state }, forceReload = false) {
            return await new Promise(function(resolve, reject) {
                giftingService.fetchDeliveryDays()
                    .then(response => {
                        commit('setDeliveryDays', response.data.properties['delivery-days']);
                        resolve();
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        fetchGiftingCoupons({ commit, rootGetters }) {
            const coupons = rootGetters['account/getCoupons'];
            const multiCoupon = coupons && coupons.multiGift;
            const singleCoupon = coupons && coupons.singleGift;
            commit('setMultiGiftCoupon', multiCoupon);
            commit('setSingleGiftCoupon', singleCoupon);
        },

        resetGifting({ commit }) {
            commit('resetState');
        },
    },
    getters: {
        getAdditionalProducts: (state) => state.giftDetails.additionalProducts,

        getAdditionalProductsTotalCost: (state) => state.giftDetails.additionalProductsCost,

        getDeliveryType: (state) => state.giftDetails.deliveryType,

        getDeliverableDateNumber: (state) => {
            let date = state.logistics.firstDeliveryDateAvailable;

            return date !== null ? moment(date).format('d') : null;
        },

        getDeliverableDateString: (state) => {
            let date = state.logistics.firstDeliveryDateAvailable;

            return date !== null ? moment(date).format('dddd') : null;
        },

        getDeliveryDateSelected: (state) => state.logistics.deliveryDateSelected,

        getFrequencyForSubscription: (state) => state.giftDetails.subscription.frequency,

        getGiftMessage: (state) => {
            return {
                to: state.message.to,
                body: state.message.body,
                from: state.message.from,
            };
        },

        getGiftNoteUrl: (state) => state.giftDetails.giftNoteUrl,

        getGiftReference: (state) => state.giftDetails.giftReference,

        getGiftType: (state) => state.giftDetails.deliveryType,

        getNotifier: (state) => state.giftDetails.contact.notifier,

        getNotifierNumber: (state) => state.giftDetails.contact.mobileNumber,

        getMessageBody: (state) => state.message.body,

        getMessageFrom: (state) => state.message.from,

        getMessageTo: (state) => state.message.to,

        getSelectedBoxName: (state) => state.giftDetails.selectedBoxName,

        getProductTypeIdFromName: (state, getters) => (name) => {
            if (name === null) name = getters.isSubscription ? PRODUCT_TYPE_MULTI : PRODUCT_TYPE_SINGLE;
            return state.productTypes?.filter((item) => item.name === name)[0].id;
        },

        getProductTypePriceFromName: (state, getters) => (name) => {
            if (name === null) name = getters.isSubscription ? PRODUCT_TYPE_MULTI : PRODUCT_TYPE_SINGLE;
            return state.productTypes?.filter((item) => item.name === name)[0].price;
        },

        getNumberOfDeliveriesForSubscription: (state, getters) => {
            if (getters.isSubscription) {
                let months = state.giftDetails.subscription.numberOfMonths;

                switch (getters.getFrequencyForSubscription) {
                case giftConsts.PLAN_WEEKLY:
                    return parseInt(months * (52 / 12));
                case giftConsts.PLAN_FORTNIGHTLY:
                    return parseInt(months * (26 / 12));
                case giftConsts.PLAN_MONTHLY:
                    return parseInt(months);
                default:
                    return 0;
                }
            }
        },

        isSubscription: (state, getters) => {
            return getters.getGiftType === giftConsts.GIFT_TYPE_SUBSCRIPTION;
        },

        getNumberOfMonthsForSubscription: (state) => state.giftDetails.subscription.numberOfMonths,

        getHavePricesFetched: state => state.prices.haveFetched,

        getPaidVasePrice: (state) => state.prices.paidVase,

        getPricePerOneOffDelivery: (state) => state.prices.oneOff,

        getPricePerSubscriptionDelivery: (state) => state.prices.subscription,

        getSubscriptionMaximumDeliveryMonthsAllowed: () => 12,

        getSubscriptionMinimumDeliveryMonthsAllowed: (state, getters) => {
            switch (getters.getFrequencyForSubscription) {
            case giftConsts.PLAN_WEEKLY:
                return 1;
            case giftConsts.PLAN_FORTNIGHTLY:
                return 1;
            case giftConsts.PLAN_MONTHLY:
                return 3;
            default:
                return 1;
            }
        },

        getSubscriptionPredictedLastDeliveryDate: (state, getters) => {
            let frequency = getters.getFrequencyForSubscription;
            let count = getters.getNumberOfDeliveriesForSubscription;
            let deliveryDay = getters.getDeliveryDateSelected;

            if (frequency !== null && count !== null && deliveryDay !== null) {
                switch (frequency) {
                case giftConsts.PLAN_WEEKLY:
                    return moment(deliveryDay)
                        .add(count, 'weeks')
                        .toDate();
                case giftConsts.PLAN_FORTNIGHTLY:
                    return moment(deliveryDay)
                        .add(count * 2, 'weeks')
                        .toDate();
                case giftConsts.PLAN_MONTHLY:
                    const deliveriesRemaining = count - 1;
                    const weeks = deliveriesRemaining * 4.33;
                    const totalWeeksRounded = Math.round(weeks);
                    const endDate = moment(deliveryDay).add(totalWeeksRounded, 'w').toDate();
                    return endDate;
                }
            }

            return null;
        },

        getTotalPriceForDeliveries: (state, getters) => {
            if (getters.isSubscription) {
                return getters.getNumberOfDeliveriesForSubscription * getters.getPricePerSubscriptionDelivery;
            }

            // If user selected one delivery, return one delivery price.
            return getters.getPricePerOneOffDelivery;
        },

        hasProcessed: state => state.giftDetails.hasProcessed,

        getDeliverableDate: (state) => {
            const dayMap = {
                'Sunday': 0,
                'Monday': 1,
                'Tuesday': 2,
                'Wednesday': 3,
                'Thursday': 4,
                'Friday': 5,
                'Saturday': 6,
            };

            return dayMap[state.giftDetails.subscription.availableDeliveryDay];
        },

        getUndeliverableDays(state) {
            let undeliverableDays = [];

            dayMap.forEach((day) => {
                let foundDay = state.deliveryDays.find((item) => item === day.day);

                if (!foundDay) {
                    undeliverableDays.push(day.index);
                }
            });

            return undeliverableDays;
        },

        isGiftingEnabled: () => !!process.env.VUE_APP_GIFTING_ENABLED,

        isPaidVaseAttached: (state) => state.giftDetails.addons.paidVase === true,

        isSectionComplete: (state) => (section) => state.sections[section],

        getIncompleteGiftingId: (state) => state.incompleteGiftingId,

        getIncompleteGifting: (state) => state.incompleteGifting,

        getSingleGiftCoupon: (state) => state.coupons.singleGift,

        getMultiGiftCoupon: (state) => state.coupons.multiGift,

    },
};
